import React, { useEffect, useState } from 'react'
import { Container } from 'react-bootstrap'
import { Column, Label, Panel, Row } from '../../components'
import CommonDataService from '../../Services/CommonDataService'
import LookupService from '../../Services/LookupService'
import ExportToCsv from '../../shared/components/ExportToCSV/ExportToCsv'
import { SelectOption } from '../../shared/components/input'
import { fomratWithCommasAndDecimals } from '../../shared/util/currencyUtils'
import CommissionReconciliationFilters from './CommissionReconciliationFilters'
import CommissionReconciliationGrid from './CommissionReconciliationGrid'
import { Labels } from './labels'
import AccountingReconciliationService from './services/AccountingReconciliationService'
import {
  IAccountingPreviewRequest,
  ICommissionReconciliationFilters,
  ICommissionReconciliationRow,
} from './models/CommissionReconciliationTypes'
import './CommissionReconciliation.scss'

/** Commission Reconciliation landing page. Owns filter state and wires
 *  PREVIEW/CLEAR/CSV to the Accounting API. */

const EMPTY_FILTERS: ICommissionReconciliationFilters = {}

// Format a Date as yyyy-MM-dd so it binds to the API's DateOnly TermDate.
const toYmd = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const buildRequest = (
  filters: ICommissionReconciliationFilters,
): IAccountingPreviewRequest => ({
  carrierId: filters.carrier || null,
  policyTypeId: filters.policyType || null,
  agencyId: filters.agency || null,
  termDate: filters.termDate ? toYmd(filters.termDate) : null,
})

// CSV mirrors the grid - null shows as a dash.
const moneyOrEmpty = (value: number | null, withDollar: boolean): string => {
  if (value === null || value === undefined) return '—'
  const isNeg = value < 0
  const abs = fomratWithCommasAndDecimals(Math.abs(value))
  return `${isNeg ? '- ' : ''}${withDollar ? '$' : ''}${abs}`
}

const csvHeaders = [
  Labels.COL_ACCOUNT,
  Labels.COL_POLICY,
  Labels.COL_CARRIER,
  Labels.COL_POLICY_TYPE,
  Labels.COL_TERM,
  Labels.COL_POLICY_PREMIUM,
  Labels.COL_ESTIMATED_COMMISSION,
  Labels.COL_IMPORTED_PREMIUM,
  Labels.COL_IMPORTED_COMMISSION,
  Labels.COL_RECONCILED,
  Labels.COL_VARIANCE,
  Labels.COL_COMPENSATION,
  Labels.COL_AGENCY,
]

const csvRow = (row: ICommissionReconciliationRow): (string | number)[] => [
  row.accountNumber,
  row.policyNumber,
  row.carrier,
  row.policyType ?? '—',
  row.term,
  moneyOrEmpty(row.policyPremium, true),
  moneyOrEmpty(row.estimatedCommission, false),
  moneyOrEmpty(row.importedPremium, false),
  moneyOrEmpty(row.importedCommission, false),
  row.reconciled,
  moneyOrEmpty(row.variance, false),
  moneyOrEmpty(row.compensation, false),
  row.agency ?? '—',
]

const CommissionReconciliationDashboard: React.FunctionComponent = () => {
  const [draftFilters, setDraftFilters] =
    useState<ICommissionReconciliationFilters>(EMPTY_FILTERS)
  const [rows, setRows] = useState<ICommissionReconciliationRow[]>([])
  const [totalRecords, setTotalRecords] = useState<number>(0)

  const [carrierOptions, setCarrierOptions] = useState<SelectOption[]>([])
  const [policyTypeOptions, setPolicyTypeOptions] = useState<SelectOption[]>([])
  const [agencyOptions, setAgencyOptions] = useState<SelectOption[]>([])

  const csvButtonRef = React.useRef<HTMLDivElement>(null)

  const runPreview = async (filters: ICommissionReconciliationFilters) => {
    try {
      const response = await AccountingReconciliationService.getPreview(
        buildRequest(filters),
      )
      setRows(response?.records ?? [])
      setTotalRecords(response?.totalRecordCount ?? 0)
    } catch {
      // Errors surface via the shared client, just leave the grid as it was.
    }
  }

  // Load dropdown options + an unfiltered preview on mount.
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [companies, policyTypes, agencies] = await Promise.all([
          CommonDataService.getCompanyList(),
          LookupService.fetchPolicyType(),
          LookupService.fetchAgencies(),
        ])
        // Carrier means Parent Company (a parent self-references) - writing
        // companies reuse the parent's name, which is where the dupes came from.
        // isInactive makes the shared Select show inactive ones as *name in red.
        setCarrierOptions(
          companies
            .filter((c) => c.id && c.description)
            .filter((c) => c.id === c.parentCompanyId)
            .map((c) => ({
              value: c.id as string,
              label: c.description as string,
              isInactive: !c.isActive,
            })),
        )
        setPolicyTypeOptions(
          policyTypes.map((p) => ({ value: p.id, label: p.lookupValue })),
        )
        // Agency lookup id, matched against Customer.AgencyId on the API.
        setAgencyOptions(
          agencies.map((a) => ({ value: a.id, label: a.lookupValue })),
        )
      } catch {
        // Errors surface via the shared client, just leave the dropdowns empty.
      }
    }
    void loadOptions()
    void runPreview(EMPTY_FILTERS)
    // eslint-disable-next-line
  }, [])

  const handlePreview = () => {
    void runPreview(draftFilters)
  }

  const handleClear = () => {
    setDraftFilters(EMPTY_FILTERS)
    void runPreview(EMPTY_FILTERS)
  }

  const handleExportCsv = () => {
    const button = csvButtonRef.current?.querySelector('button')
    button?.click()
  }

  const visibleRecords = rows.length

  // Drop the "of <total>" when nothing matched, it only reads right with a subset.
  const resultsHeading =
    visibleRecords === 0
      ? `${Labels.RESULTS_PREVIEW}  0 ${Labels.RECORDS_SUFFIX}`
      : `${Labels.RESULTS_PREVIEW}  ${visibleRecords.toLocaleString(
          'en-US',
        )} of ${totalRecords.toLocaleString('en-US')} ${Labels.RECORDS_SUFFIX}`

  return (
    <div className="commission-reconciliation-page">
      <CommissionReconciliationFilters
        draftFilters={draftFilters}
        onDraftChange={setDraftFilters}
        onPreview={handlePreview}
        onClear={handleClear}
        onExportCsv={handleExportCsv}
        isExportDisabled={visibleRecords === 0}
        carrierOptions={carrierOptions}
        policyTypeOptions={policyTypeOptions}
        agencyOptions={agencyOptions}
      />

      {/* Results heading + grid, same layout as Customer Results. */}
      <Row>
        <Column md={12}>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Label
              text={resultsHeading}
              className="fs-6 poppins-bold labelStyle labelPanelHeading align-self-center ms-0 mb-0"
            />
          </div>
        </Column>
      </Row>
      <Panel className="mt-1 border-top-0" cardBodyClassName="p-0">
        <Container fluid>
          <Row>
            <CommissionReconciliationGrid rows={rows} />
          </Row>
        </Container>
      </Panel>

      <div ref={csvButtonRef} style={{ display: 'none' }}>
        {/* Disabled here too - handleExportCsv clicks this one, so gating it is
            what actually blocks an empty export. */}
        <ExportToCsv
          data={rows}
          headers={csvHeaders}
          fileName={Labels.CSV_FILENAME}
          getRow={csvRow}
          buttonLabel={Labels.CSV_EXPORT}
          disabled={visibleRecords === 0}
        />
      </div>
    </div>
  )
}

export default CommissionReconciliationDashboard
